import api from "../config/api";

const DASHBOARD_STAT_FIELDS = [
  'totalCustomers',
  'totalShops',
  'activeShops',
  'totalStaff',
  'totalBookings',
  'todayBookings',
  'pendingBookings',
  'totalRevenue'
];

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

export const isValidDashboardStats = (data) => (
  data
  && typeof data === 'object'
  && !Array.isArray(data)
  && DASHBOARD_STAT_FIELDS.every(field => isFiniteNumber(data[field]))
  && Array.isArray(data.recentActivities)
  && Array.isArray(data.systemHealth)
);

class ApiService {
  static async getBookingById(bookingId) {
    return this.makeRequest(`/bookings/${bookingId}`);
  }

  // Get bookings by status
  static async getBookingsByStatus(status) {
    return this.makeRequest(`/bookings/status/${status}`);
  }

  // Services
  static async getServices() {
    return (await api.get('/service')).data;
  }

  static async getServiceById(id) {
    return (await api.get(`/service/${id}`)).data;
  }

  static async getServicesByShop(shopId) {
    return (await api.get(`/service/shop/${shopId}`)).data;
  }

  static async createService(serviceData) {
    return (await api.post('/service', serviceData)).data;
  }

  static async updateService(id, serviceData) {
    return (await api.put(`/service/${id}`, serviceData)).data;
  }

  static async deleteService(id) {
    return (await api.delete(`/service/${id}`)).data;
  }
  // Helper method for making API requests
  static async makeRequest(endpoint, options = {}) {
    try {
      const method = (options.method || 'GET').toLowerCase();
      const config = { headers: { 'Content-Type': 'application/json', ...(options.headers || {}) } };

      let res;
      if (method === 'get') res = await api.get(endpoint, config);
      else if (method === 'post') res = await api.post(endpoint, options.body ? JSON.parse(options.body) : undefined, config);
      else if (method === 'put') res = await api.put(endpoint, options.body ? JSON.parse(options.body) : undefined, config);
      else if (method === 'delete') res = await api.delete(endpoint, config);
      else res = await api.request({ url: endpoint, method, data: options.body ? JSON.parse(options.body) : undefined, ...config });

      return res.data;
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw error;
    }
  }

  // Dashboard Statistics
  static async getDashboardStats() {
    const dashboardData = await this.makeRequest('/superadmin/dashboard/stats');
    if (!isValidDashboardStats(dashboardData)) {
      throw new Error('Invalid dashboard statistics response.');
    }
    return dashboardData;
  }

  static async getRevenueAnalytics(from, to) {
    const response = await api.get('/superadmin/revenue-analytics', { params: { from, to } });
    return response.data;
  }

  // Recent Activities
  static async getRecentActivities() {
    const dashboardData = await this.makeRequest('/superadmin/dashboard/stats');
    if (!isValidDashboardStats(dashboardData)) {
      throw new Error('Invalid recent activity response.');
    }
    return dashboardData.recentActivities;
  }

  // System Health Check
  static async getSystemHealth() {
    const dashboardData = await this.makeRequest('/superadmin/dashboard/stats');
    if (!isValidDashboardStats(dashboardData)) {
      throw new Error('Invalid system health response.');
    }
    return dashboardData.systemHealth;
  }

  // Get detailed data for specific sections
  static async getShops() {
    return this.makeRequest('/shop');
  }

  static async getCustomers() {
    return this.makeRequest('/customers');
  }

  static async getBookings() {
    return this.makeRequest('/bookings');
  }

  static async getAdmins() {
    return this.makeRequest('/admin');
  }

  static async getAdminById(adminId) {
    return this.makeRequest(`/admin/${adminId}`);
  }

  static async createAdmin(adminData) {
    return this.makeRequest('/admin/register', {
      method: 'POST',
      body: JSON.stringify(adminData),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  static async updateAdmin(adminId, adminData) {
    return this.makeRequest(`/admin/${adminId}`, {
      method: 'PUT',
      body: JSON.stringify(adminData),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  static async deleteAdmin(adminId) {
    return this.makeRequest(`/admin/${adminId}`, {
      method: 'DELETE'
    });
  }

  // Shop Management Methods
  static async createShop(shopData, imageFile) {
    let body, headers;
    if (imageFile) {
      body = new FormData();
      Object.entries(shopData).forEach(([key, value]) => body.append(key, value));
      body.append('shopImage', imageFile);
      headers = {};
    } else {
      body = JSON.stringify(shopData);
      headers = { 'Content-Type': 'application/json' };
    }
    if (imageFile) {
      // multipart/form-data; axios will set correct headers
      return (await api.post('/shop', body)).data;
    } else {
      return (await api.post('/shop', shopData, { headers })).data;
    }
  }

  static async updateShop(shopId, shopData, imageFile) {
    let body, headers;
    if (imageFile) {
      body = new FormData();
      Object.entries(shopData).forEach(([key, value]) => body.append(key, value));
      body.append('shopImage', imageFile);
      headers = {};
    } else {
      body = JSON.stringify(shopData);
      headers = { 'Content-Type': 'application/json' };
    }
    if (imageFile) {
      return (await api.put(`/shop/${shopId}`, body)).data;
    } else {
      return (await api.put(`/shop/${shopId}`, shopData, { headers })).data;
    }
  }

  static async rejectShop(shopId, reason) {
    return (await api.post(`/shop/${shopId}/reject`, { reason })).data;
  }

  static async deleteShop(shopId) {
    return (await api.delete(`/shop/${shopId}`)).data;
  }

  static async getShopById(shopId) {
    return (await api.get(`/shop/${shopId}`)).data;
  }

  static async getShopDocuments(shopId) {
    return (await api.get(`/shop/${shopId}/documents`)).data;
  }

  static async getSavedShopDocuments() {
    return (await api.get('/superadmin/shop-documents')).data;
  }

  static async deleteSavedShopDocument(documentId) {
    return (await api.delete(`/superadmin/shop-documents/${documentId}`)).data;
  }

  static async deleteAllOrphanedDocuments() {
    return (await api.delete('/superadmin/shop-documents/orphaned/all')).data;
  }

  static async cleanupRejectedDocuments({ olderThanDays = 30, dryRun = true } = {}) {
    return (await api.post('/superadmin/cleanup-rejected-documents', { olderThanDays, dryRun })).data;
  }

  static async reviewShopDocument(documentId, status, rejectionReason = '') {
    return (await api.patch(`/shop/documents/${documentId}/review`, {
      status,
      rejection_reason: rejectionReason,
    })).data;
  }

  // Customer Management Methods
  static async updateCustomer(customerId, customerData) {
    return (await api.put(`/customers/${customerId}`, customerData)).data;
  }

  static async getCustomerById(customerId) {
    return (await api.get(`/customers/${customerId}`)).data;
  }

  // Helper method to format time ago
  static formatTimeAgo(dateString) {
    if (!dateString) return 'Unknown';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffInMs = now - date;
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMins < 1) return 'Just now';
    if (diffInMins < 60) return `${diffInMins} minute${diffInMins > 1 ? 's' : ''} ago`;
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  }

  // Format currency
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  }
}

export default ApiService;